import axios from 'axios';
import axiosRetry from 'axios-retry';
import applyInterceptors from '../interceptors';
import { API_KEYS } from '../common/consts';
import { RequestError } from '../common/errors';
import Dibels8ContentClient from './Dibels8ContentClient';
import Dibels8ResultsClient from './Dibels8ResultsClient';
import Dibels8ResultsClientWithKeycloak from './Dibels8ResultsClientWithKeycloak';
import Dibels8TranslatorClient from './Dibels8TranslatorClient';
import Dibels8ZoGClient from './Dibels8ZoGClient';
import Dibels8StaticContentClient from './Dibels8StaticContentClient';
import Dibels8ApplicationContentClient from './Dibels8ApplicationContentClient';

const axiosInstance = axios.create();
applyInterceptors(axiosInstance);

// Setup retries with a custom retry condition
axiosRetry(axiosInstance, {
    retries: 3,
    retryCondition: error => {
        return (
            error.code === 'ECONNABORTED' ||
            (error.response &&
                error.response.status >= 500 &&
                error.response.status <= 599 &&
                /\/assessment-forms-api\/v1\/secure-forms/.test(error.config.url))
        ); // only for secure forms
    },
});

const dibels8ContentClient = new Dibels8ContentClient(axiosInstance);
const dibels8ResultsClient = new Dibels8ResultsClient(axiosInstance);
const dibels8ResultsClientWithKeycloak = new Dibels8ResultsClientWithKeycloak(axiosInstance);
const dibels8TranslatorClient = new Dibels8TranslatorClient(axiosInstance);
const dibels8ZoGClient = new Dibels8ZoGClient(axiosInstance);
const dibels8StaticContentClient = new Dibels8StaticContentClient(axiosInstance);
const dibels8ApplicationContentClient = new Dibels8ApplicationContentClient(axiosInstance);

const assessment = 'DIBELS8';

/**
 * @param {(String)[]}  studentUids    - Array of students uids
 * @param {String}      schoolYear     - School year, optional parameter. (E.g. for 18/19 it is 2019)
 * @param {String}      classId        - class id, optional parameter.
 * @param {String}      instContext    - institution id, optional paramenter
 * @param {String}      token          - keycloak token for API authorization
 * @param {String}      staffId        - staffId for an additional header used in results caching.
 *
 * @param {String} language - does not necessary property - need only for OWAU structure
 * @return {Promise<{results: Object[], studentStatuses: Object[]}, studentGrowthZones: Object[] >}
 *          results, student statuses and zones of growth from Translator API
 */
export const getDibels8ResultsAndStatuses = async (
    studentUids,
    schoolYear,
    classId,
    instContext,
    token,
    staffId = '',
    // eslint-disable-next-line no-unused-vars
    language = '',
) => {
    try {
        return await dibels8TranslatorClient.getResults(
            studentUids,
            schoolYear,
            classId,
            instContext,
            true,
            true,
            true,
            token,
            staffId,
        );
    } catch (error) {
        throw new RequestError(`${assessment} get results request failed`, error, assessment);
    }
};

/**
 * Passes through given results array and decide whether the result should be created or updated
 */
export const persistDibels8Results = async results => {
    try {
        const response = await dibels8ResultsClient.persistResults(results);
        if (Array.isArray(response)) {
            return response.map(resultResponse =>
                resultResponse instanceof Error
                    ? new RequestError(
                          `${assessment} persist result request failed`,
                          resultResponse,
                          assessment,
                      )
                    : resultResponse,
            );
        }
        throw new Error(`Incorrect response format: ${response}`);
    } catch (error) {
        throw new RequestError(`${assessment} results saving failed`, error, assessment);
    }
};

export const persistDibels8ResultsWithKeycloak = async (results, token) => {
    try {
        dibels8ResultsClientWithKeycloak.setToken(token);
        const response = await dibels8ResultsClientWithKeycloak.persistResults(results);
        if (Array.isArray(response)) {
            return response.map(resultResponse =>
                resultResponse instanceof Error
                    ? new RequestError(
                          `${assessment} persist result request failed`,
                          resultResponse,
                          assessment,
                      )
                    : resultResponse,
            );
        }
        throw new Error(`Incorrect response format: ${response}`);
    } catch (error) {
        throw new RequestError(`${assessment} results saving failed`, error, assessment);
    }
};

/**
 * @return {{ forms: Object[], cutscores: Object[], reminders: Object[], instructions: Object[] }} dibels8 content
 * (forms, cutscores, reminders, instructions)
 */
export const getDibels8Content = async () => {
    try {
        const [forms, cutscores, reminders, instructions] = await Promise.all([
            dibels8ContentClient.getForms(),
            dibels8ContentClient.getCutscores(),
            dibels8StaticContentClient.getReminders(),
            dibels8StaticContentClient.getInstructions(),
        ]);

        return {
            [API_KEYS.FORMS]: forms,
            [API_KEYS.CUTSCORES]: cutscores,
            [API_KEYS.REMINDERS]: reminders,
            [API_KEYS.INSTRUCTIONS]: instructions,
        };
    } catch (error) {
        throw new RequestError(`${assessment} get content request failed`, error, assessment);
    }
};

/**
 * @param {(String)[]}  studentUids    - Array of students uids
 * @param {String}      schoolYear     - School year, optional parameter. (E.g. for 18/19 it is 2019)
 *
 * @param token keycloak token
 * @return {Promise<{ Object[]} } list of student goals or empty list
 */
export const getDibels8StudentGoals = async (studentUids, schoolYear, token) => {
    try {
        return await dibels8ZoGClient.getStudentGoals(studentUids, schoolYear, token);
    } catch (error) {
        throw new RequestError(`${assessment} get student goals request failed`, error, assessment);
    }
};

/**
 * @param {String} token - keycloak token for API authorization
 * @returns {Promise<Object>} Dibels8 forms
 */
export const getDibels8Forms = async token => {
    let forms;

    try {
        forms = await dibels8ContentClient.getForms([], token);
    } catch (error) {
        throw new RequestError(`${assessment} get forms request failed`, error, assessment);
    }

    return forms || [];
};

/**
 * Retrieve secure form by form uid.
 * @param {String} formUid secure form uid
 * @param {Number} year - school year
 * @param {String} token - keycloak token for API authorization
 * @return {Object[]} list of forms objects
 */
export const getDibels8SecureForms = async (formUid, year, token) => {
    let form;

    try {
        form = await dibels8ApplicationContentClient.getSecureForm(formUid, year, token);
    } catch (error) {
        throw new RequestError(`${assessment} get forms request failed`, error, assessment);
    }

    return form || [];
};

/**
 * @param {String} token - keycloak token for API authorization
 * @returns {Promise<Object>} Dibels8 cutscores
 */
export const getDibels8Cutscores = async token => {
    let cutscores;
    try {
        cutscores = await dibels8ContentClient.getCutscores([], token);
    } catch (error) {
        throw new RequestError(`${assessment} get cutscores request failed`, error, assessment);
    }

    return cutscores || [];
};

/**
 * @param {String} token - keycloak token for API authorization
 * @returns {Promise<Object>} Dibels8 reminders
 */
export const getDibels8Reminders = async token => {
    let reminders;
    try {
        reminders = await dibels8StaticContentClient.getReminders(token);
    } catch (error) {
        throw new RequestError(`${assessment} get reminders request failed`, error, assessment);
    }

    return reminders || [];
};

/**
 * @param {String} token - keycloak token for API authorization
 * @returns {Promise<Object>} Dibels8 instructions
 */
export const getDibels8Instructions = async token => {
    let instructions;
    try {
        instructions = await dibels8StaticContentClient.getInstructions(token);
    } catch (error) {
        throw new RequestError(`${assessment} get instructions request failed`, error, assessment);
    }

    return instructions || [];
};

export {
    Dibels8ContentClient,
    Dibels8ResultsClient,
    Dibels8ResultsClientWithKeycloak,
    Dibels8StaticContentClient,
    Dibels8TranslatorClient,
    Dibels8ZoGClient,
    Dibels8ApplicationContentClient,
    dibels8ApplicationContentClient,
    applyInterceptors,
    RequestError,
    API_KEYS,
};
